/**
 * Global cross-context notifier.
 *
 * Subscribes to the current user's personal WS channel (`user:{user_id}`) and
 * shows a RICH pop-up card (sender avatar + preview + prominent CTA) whenever:
 *   • a DM arrives while the user is anywhere except the Mail page,
 *   • a planet chat message arrives in a joined planet they're not viewing,
 *   • someone @-mentions them anywhere,
 *   • a gift or date-invite lands.
 *
 * UX layer:
 *   • Custom rendered toast card (not sonner's compact default) so avatars +
 *     preview text are properly readable in the middle of a busy chat.
 *   • Subtle audio chime via WebAudio (no external asset needed).
 *   • Mobile haptic feedback (navigator.vibrate) when supported.
 *   • Browser Notification API fallback when the tab is unfocused / hidden.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift, Heart, AtSign, Mail, MessageCircle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChannel } from '../lib/ws';
import { startPushNotifications } from '../lib/push';

// --- Attention effects ---------------------------------------------------
// Play a short cosmic chime whenever a personal ping arrives. Uses WebAudio
// so we don't ship an audio asset. Silent-fails if AudioContext is blocked.
let _audioCtx = null;
function playChime() {
  try {
    if (!_audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      _audioCtx = new Ctx();
    }
    if (_audioCtx.state === 'suspended') _audioCtx.resume().catch(() => {});
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    // Two short chirps ~ 880Hz → 1320Hz for a friendly ding
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.25);
    });
  } catch { /* silent */ }
}

function hapticTap() {
  try { navigator.vibrate?.(12); } catch { /* silent */ }
}

// Fires a native browser notification IF the tab is hidden and permission is
// granted (or grantable). No prompt if the user hasn't opted in yet.
function maybeNativeNotify(title, body) {
  try {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico', badge: '/favicon.ico' });
    }
  } catch { /* silent */ }
}

// --- Rich card renderer --------------------------------------------------
// Glass-morphic card with sender avatar, preview and CTA. Falls back to
// initials when avatar is null/data-uri-stripped. Colour hints the source.
function RichPingCard({ id, tone, icon, title, preview, senderName, senderAvatar, cta, onCta, onClose }) {
  const initials = (senderName || 'FV').slice(0, 2).toUpperCase();
  const borderMap = {
    dm: 'border-cyan-400/50',
    planet: 'border-fuchsia-400/50',
    mention: 'border-amber-400/60',
    gift: 'border-amber-300/60',
    date: 'border-pink-400/50',
  };
  const gradMap = {
    dm: 'from-cyan-500/25 to-transparent',
    planet: 'from-fuchsia-500/25 to-transparent',
    mention: 'from-amber-400/25 to-transparent',
    gift: 'from-amber-300/25 to-transparent',
    date: 'from-pink-400/25 to-transparent',
  };
  return (
    <div
      role="alert"
      data-testid={`live-ping-${tone}`}
      className={`relative pointer-events-auto w-[340px] max-w-[92vw] rounded-2xl border ${borderMap[tone] || 'border-white/15'} bg-[#0F0F19]/95 backdrop-blur-xl shadow-2xl overflow-hidden`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradMap[tone] || ''} opacity-70 pointer-events-none`} />
      <div className="relative flex items-start gap-3 p-3">
        {/* Sender avatar / icon badge */}
        <div className="relative shrink-0">
          <div className={`w-11 h-11 rounded-full overflow-hidden border-2 border-white/20 bg-gradient-to-br from-fuchsia-500/50 to-cyan-500/50 flex items-center justify-center`}>
            {senderAvatar
              ? <img src={senderAvatar} alt="" className="w-full h-full object-cover" />
              : <span className="text-[13px] font-bold text-white/90">{initials}</span>}
          </div>
          {/* Kind badge in the corner */}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#0F0F19] border border-white/15 flex items-center justify-center">
            {icon}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-white leading-tight truncate">{title}</div>
          {preview && (
            <div className="text-[12px] text-white/70 mt-0.5 line-clamp-2 leading-snug">{preview}</div>
          )}
          {cta && (
            <button
              type="button"
              onClick={() => { onCta?.(); toast.dismiss(id); }}
              data-testid={`live-ping-cta-${tone}`}
              className="mt-2 px-3 py-1.5 rounded-full text-[11px] font-semibold text-black bg-gradient-to-r from-fuchsia-400 to-cyan-400 hover:brightness-110 transition"
            >
              {cta}
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => { onClose?.(); toast.dismiss(id); }}
          className="shrink-0 w-6 h-6 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 flex items-center justify-center transition"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function showRichPing({ tone, icon, title, preview, senderName, senderAvatar, cta, onCta, duration = 8000, nativeBody }) {
  toast.custom(
    (id) => (
      <RichPingCard
        id={id}
        tone={tone}
        icon={icon}
        title={title}
        preview={preview}
        senderName={senderName}
        senderAvatar={senderAvatar}
        cta={cta}
        onCta={onCta}
      />
    ),
    { duration, unstyled: true, className: 'wv-live-ping' }
  );
  playChime();
  hapticTap();
  maybeNativeNotify(title, nativeBody || preview || '');
}

export default function LiveNotifier() {
  const { user, refreshCounts } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const userId = user?.user_id;

  // Keep the latest pathname in a ref so the WS handler always reads the
  // current route without re-subscribing on every navigation.
  const pathRef = useRef(loc.pathname);
  useEffect(() => { pathRef.current = loc.pathname; }, [loc.pathname]);

  // Boot-time diagnostic + best-effort request to grant native notifications.
  useEffect(() => {
    if (!userId) return;
    console.debug(`[LiveNotifier] subscribed to user:${userId}`);
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        // Silent request — browser will show its own permission prompt UI
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* silent */ }
    // Native mobile push (Capacitor) — silent no-op on web
    startPushNotifications(nav);
  }, [userId, nav]);

  useChannel(userId ? `user:${userId}` : null, (msg) => {
    if (!msg || msg.type !== 'notification.new') return;
    const path = pathRef.current || '';
    console.debug('[LiveNotifier] received', msg.kind, msg);

    if (msg.kind === 'dm_message') {
      // Suppress ONLY if the user is on the Mail page — they'll see it inline.
      if (path === '/app/messages' || path.startsWith('/app/messages/')) return;
      const preview = (msg.preview || '').trim() || 'Sent you a message';
      showRichPing({
        tone: 'dm',
        icon: <Mail size={10} className="text-cyan-300" />,
        title: `${msg.sender_name || 'Someone'} messaged you`,
        preview,
        senderName: msg.sender_name,
        senderAvatar: msg.sender_avatar,
        cta: 'Open chat',
        onCta: () => nav('/app/messages'),
        duration: 10000,
        nativeBody: `${msg.sender_name || 'Someone'}: ${preview}`,
      });
      refreshCounts?.();
      return;
    }

    if (msg.kind === 'planet_message') {
      const planetPath = `/app/planets/${msg.planet_id}`;
      if (path === planetPath) return;
      const preview = (msg.preview || '').trim();
      showRichPing({
        tone: 'planet',
        icon: <MessageCircle size={10} className="text-fuchsia-300" />,
        title: `${msg.planet_emoji ? msg.planet_emoji + ' ' : ''}${msg.planet_name || 'Planet'} — new chat`,
        preview: `${msg.sender_name || 'Someone'}: ${preview || 'sent a message'}`,
        senderName: msg.sender_name,
        cta: 'Open',
        onCta: () => nav(planetPath),
        duration: 6000,
      });
      return;
    }

    if (msg.kind === 'mention') {
      // Priority toast — ALWAYS shows, even if inside that planet's chat.
      const planetPath = `/app/planets/${msg.planet_id}`;
      const preview = (msg.preview || '').trim() || `in ${msg.planet_name || 'a planet'}`;
      showRichPing({
        tone: 'mention',
        icon: <AtSign size={10} className="text-amber-300" />,
        title: `${msg.sender_name || 'Someone'} tagged you`,
        preview: `${msg.planet_emoji ? msg.planet_emoji + ' ' : ''}${msg.planet_name || 'planet'}: ${preview}`,
        senderName: msg.sender_name,
        senderAvatar: msg.sender_avatar,
        cta: 'Jump in',
        onCta: () => nav(planetPath),
        duration: 12000,
      });
      refreshCounts?.();
      return;
    }

    if (msg.kind === 'gift') {
      showRichPing({
        tone: 'gift',
        icon: <Gift size={10} className="text-amber-300" />,
        title: 'You received a gift 🎁',
        preview: msg.sender_name ? `From ${msg.sender_name}` : 'Someone sent you a gift',
        senderName: msg.sender_name,
        cta: 'View',
        onCta: () => nav('/app/notifications'),
        duration: 8000,
      });
      refreshCounts?.();
      return;
    }

    if (msg.kind === 'date_invite') {
      showRichPing({
        tone: 'date',
        icon: <Heart size={10} className="text-pink-300" />,
        title: 'New date invite 💝',
        preview: `${msg.sender_name || 'Someone'} sent you a date invite`,
        senderName: msg.sender_name,
        cta: 'Open',
        onCta: () => nav('/app/messages'),
        duration: 10000,
      });
      refreshCounts?.();
      return;
    }

    // Fallback for any other kinds — refresh badges silently.
    refreshCounts?.();
  }, [userId]);

  return null;
}
